import React from 'react';
import logger from '@lib/logger';
import { ApolloClient, addApolloState, getApollo } from '@lib/apollo/apolloClient';
import useUpdatePreview from '@lib/prismic/util/useUpdatePreviewRef';
import Compose from '@components/shared/Compose';
import LoginRegisterModalProvider
  from '@components/shared/providers/LoginRegisterModalProvider/LoginRegisterModalProvider';
import ShareModalProvider from '@components/shared/providers/ShareModalProvider/ShareModalProvider';
import ExploreProductModalProvider
  from '@components/shared/providers/ExploreProductModalProvider/ExploreProductModalProvider';
import InteriorDesignersHomePage
  from '@components/pages/InteriorDesignerHomePage/InteriorDesignersHomePage';

import AVAILABLE_DESIGNERS_QUERY, {
  AvailableDesignersResponse,
  AvailableDesignersVariables,
  Designer,
  INTERIOR_DESIGNER_PAGE_SIZE
} from '@graphql/Designer/queries/availableDesigners.graphql';
import { GetServerSidePropsContext } from 'next';
import { fetchLocationDocUIDs } from '@lib/prismic/fetchLocationDocs';
import { withAuthenticatedUser } from '@lib/user/auth';
import fetchSinglePrismicDocument from '@lib/prismic/fetchSinglePrismicDocument';

interface IInteriorDesignersPageProps {
  cities: string[];
  designers: Designer[];
  previewRef: any;
  prismicDoc: any;
  roomTypeIds: any;
  styleIds: any;
}

export default function InteriorDesignersPage(props: IInteriorDesignersPageProps) {
  const {
    cities,
    designers,
    previewRef,
    prismicDoc,
    roomTypeIds,
    styleIds
  } = props;
  useUpdatePreview(previewRef, prismicDoc.id);

  return (
    <Compose
      components={[
        LoginRegisterModalProvider,
        ShareModalProvider,
        ExploreProductModalProvider,
      ]}
    >
      <InteriorDesignersHomePage
        cities={cities}
        designers={designers}
        prismicData={prismicDoc.data}
        roomTypeIds={roomTypeIds}
        styleIds={styleIds}
      />
    </Compose>
  );
}

const PRISMIC_TYPE = 'interior-designer-home-page';

export const getServerSideProps = withAuthenticatedUser(async ({
  user
}, context: GetServerSidePropsContext) => {
  const log = logger(PRISMIC_TYPE);
  const { previewData } = context;

  const prismicDoc = await fetchSinglePrismicDocument(PRISMIC_TYPE, { previewData });
  if (!prismicDoc) {
    log.error(`Unable to load data from prismic`);
    return { notFound: true };
  }

  const { data = {} } = prismicDoc;
  const previewRef = previewData ? (previewData as any).ref : null;

  const apolloClient = getApollo({
    jwt: user?.jwt,
    cacheEnabled: true,
  });

  // eslint-disable-next-line camelcase
  const { designer_style_ids = [], designer_room_type_ids = [] } = data;

  const styleIds = designer_style_ids
    .map((ds: any) => ds.designer_style_id)
    .filter((ds: any) => !!ds);

  const roomTypeIds = designer_room_type_ids
    .map((rt: any) => rt.designer_room_type_id)
    .filter((rt: any) => !!rt);

  const designersPromiseResult = await fetchDesignersFromPrismicDoc(
    styleIds,
    roomTypeIds,
    apolloClient
  );

  let designers: Designer[] = [];

  const cities = await fetchLocationDocUIDs();

  if (designersPromiseResult && !!designersPromiseResult.length) {
    designers = designersPromiseResult;
  } else {
    log.error('Interior Designer Home page failed to grab designers');
  }

  return addApolloState(apolloClient,
    {
      props: {
        prismicDoc,
        previewRef,
        cities,
        designers,
        roomTypeIds,
        styleIds,
        user
      },
    });
}, {
  requireAuth: false
});

async function fetchDesignersFromPrismicDoc<T>(
  styleIds: number[],
  roomTypeIds: number[],
  apolloClient: ApolloClient<T>
): Promise<Designer[]> {
  const variables: AvailableDesignersVariables = {
    pageNumber: 1,
    pageSize: INTERIOR_DESIGNER_PAGE_SIZE,
    filters: {
      styleIds,
      roomTypeIds
    }
  };

  const designersResponse = await apolloClient
    .query<AvailableDesignersResponse, AvailableDesignersVariables>({
      query: AVAILABLE_DESIGNERS_QUERY,
      variables,
    });

  return designersResponse.data?.availableDesigners?.items || [];
}
